import React from 'react';
import { Flame, Calendar, User, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Bill } from '../../types/bill';
import CalendarEventDisplay from '../shared/CalendarEventDisplay';
import { hasUpcomingCalendarEvent } from '../../utils/calendarUtils';
import { formatBillStatusShort } from '../../utils/formatBillStatus';
import ProMetaRow from './ProMetaRow';
import { getPositionColorClasses } from '../../utils/positionColors';

export interface ProMobileBillCardProps {
  /** Bill data */
  bill: Bill;
  /** Whether this is a hot bill (shows fire icon) */
  isHotBill?: boolean;
  /** Borderless flush layout for hot bills nested inside a parent ProCard (dashboard). */
  flush?: boolean;
  /** Workspace position chip (Support, Monitor, etc.) */
  position?: string;
  /** Workspace name if tracked */
  workspaceName?: string;
  /** Click handler */
  onClick?: (bill: Bill) => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Pro-namespaced mobile bill card.
 *
 * Fork of the shared `components/mobile/MobileBillCard` that adds light-mode
 * contrast tuned for the Pro app (white background, darker status text, soft
 * hot-bill tint). The shared component preserves its original dark-only look
 * for the citizen app.
 *
 * Features:
 * - Bill number with optional fire icon for hot bills
 * - Bill name/title with light/dark text contrast
 * - Status badge with light-mode-readable colors
 * - Sponsor name
 * - Committee
 * - Upcoming activities
 * - Hot bills: borderless, flush rows inside the parent card; fire icon accent
 */
const ProMobileBillCard: React.FC<ProMobileBillCardProps> = ({
  bill,
  isHotBill = false,
  flush = false,
  position,
  workspaceName,
  onClick,
  className = ''
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick(bill);
    } else {
      navigate(`/legislation/${bill.id || bill.billNumber}`);
    }
  };

  // Get the bill's primary sponsor
  const getPrimarySponsor = () => {
    if (bill.sponsors && bill.sponsors.length > 0) {
      const primary = bill.sponsors.find(s => s.sponsor_type === 'primary' || s.type === 'primary');
      return primary?.name || bill.sponsors[0]?.name || 'Unknown';
    }
    return 'Unknown';
  };

  // Get the committee name
  const getCommittee = () => {
    if (bill.committees && bill.committees.length > 0) {
      return bill.committees[0]?.name || null;
    }
    if (bill.location) return bill.location;
    return null;
  };

  // Get status display
  const getStatus = () => {
    return bill.status || 'Unknown';
  };

  // Get status color — darker shades for light mode, lighter for dark
  const getStatusColor = () => {
    const status = getStatus().toLowerCase();
    if (status.includes('signed') || status.includes('enacted')) {
      return 'text-green-700 dark:text-green-400';
    }
    if (status.includes('passed') || status.includes('approved')) {
      return 'text-blue-700 dark:text-blue-400';
    }
    if (status.includes('failed') || status.includes('vetoed') || status.includes('dead')) {
      return 'text-red-700 dark:text-red-400';
    }
    if (status.includes('committee') || status.includes('hearing')) {
      return 'text-amber-700 dark:text-amber-400';
    }
    return 'text-text-secondary dark:text-text-dark-secondary';
  };

  const committee = getCommittee();
  const sponsor = getPrimarySponsor();
  const statusLabel = formatBillStatusShort(getStatus());
  const showSponsor = sponsor !== 'Unknown';

  return (
    <button
      onClick={handleClick}
      className={`
        w-full text-left overflow-hidden transition-all duration-200
        active:scale-[0.98] touch-manipulation
        ${flush
          ? 'pro-hot-bill-card rounded-lg border-none shadow-none outline-none ring-0'
          : 'rounded-xl bg-white dark:bg-[var(--pro-surface-card)] border border-border-light dark:border-white/10 shadow-sm'
        }
        ${className}
      `}
    >
      {/* Card content */}
      <div className={`relative ${flush ? 'py-3 px-0' : 'p-4'}`}>
        {/* Bill number with optional fire icon + position chip */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            {isHotBill && (
              <Flame className="w-4 h-4 text-primary-600 dark:text-primary-300 flex-shrink-0" />
            )}
            <span
              className={`${flush ? 'text-sm' : 'text-base'} font-bold truncate ${flush ? 'text-primary-700 dark:text-primary-300' : 'text-primary-600 dark:text-orange-400'}`}
            >
              {bill.billNumber}
            </span>
          </div>
          {position && (
            <span
              className={`flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${getPositionColorClasses(position)}`}
            >
              {position}
            </span>
          )}
        </div>

        {/* Bill Title */}
        <h3 className="font-medium text-sm leading-snug mb-3 line-clamp-2 text-text-primary dark:text-white">
          {bill.title || 'Untitled Bill'}
        </h3>

        {/* Status */}
        <div className="mb-3">
          <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary uppercase tracking-wide">Status</span>
          <p className={`text-sm font-semibold leading-snug ${getStatusColor()}`}>
            {statusLabel}
          </p>
        </div>

        {(showSponsor || workspaceName) && (
          <ProMetaRow
            leadingIcon={<User className="w-3 h-3" />}
            segments={[showSponsor ? sponsor : null, workspaceName]}
            className="mb-2"
          />
        )}

        {/* Committee */}
        {committee && (
          <div className="flex items-center gap-2 mb-2 text-sm text-text-secondary dark:text-text-dark-secondary">
            <Building2 className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">{committee}</span>
          </div>
        )}

        {/* Upcoming Activities */}
        {hasUpcomingCalendarEvent(bill.calendarEvents) && (
          <div
            className={`flex items-start gap-2 mt-3 pt-3 border-t ${
              isHotBill
                ? 'border-border-light dark:border-white/5'
                : 'border-border-light dark:border-white/10'
            }`}
          >
            <Calendar className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-text-tertiary dark:text-text-dark-tertiary" />
            <div className="flex-1">
              <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary uppercase tracking-wide block mb-1">
                Upcoming
              </span>
              <CalendarEventDisplay
                events={bill.calendarEvents}
                variant="inline"
                maxDisplay={1}
                context="authenticated"
              />
            </div>
          </div>
        )}
      </div>
    </button>
  );
};

export default ProMobileBillCard;
